import { z } from 'zod';

// Empty strings count as "not set" — without this, an .env.local entry like
// `RESEND_API_KEY=` would satisfy `.optional()` (since the var IS defined) but
// fail downstream when consumers try to use it.
const optionalString = z.preprocess(
  (v) => (typeof v === 'string' && v.length === 0 ? undefined : v),
  z.string().min(1).optional(),
);
const optionalUrl = z.preprocess(
  (v) => (typeof v === 'string' && v.length === 0 ? undefined : v),
  z.string().url().optional(),
);

const serverSchema = z.object({
  NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
  NEXT_PUBLIC_TURNSTILE_SITE_KEY: optionalString,
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1),
  // The following are required in production but no-op in dev. Consumers
  // (captcha route, resend client, rate-limiter) must handle missing values
  // and degrade gracefully — usually by skipping the feature.
  RESEND_API_KEY: optionalString,
  RESEND_FROM_DOMAIN: optionalString,
  INTERNAL_NOTIFY_EMAILS: z
    .string()
    .optional()
    .transform((s) =>
      (s ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  APP_URL: z.string().url(),
  UPSTASH_REDIS_URL: optionalUrl,
  UPSTASH_REDIS_TOKEN: optionalString,
  TURNSTILE_SECRET_KEY: optionalString,
  ACM_CLIENT_ID: optionalString,
  ACM_CLIENT_SECRET: optionalString,
  ACM_API_USERNAME: optionalString,
  ACM_API_PASSWORD: optionalString,
  ACM_CUSTOMER_ID: optionalString,
});

const clientSchema = z.object({
  NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
  NEXT_PUBLIC_TURNSTILE_SITE_KEY: optionalString,
});

export type ServerEnv = z.infer<typeof serverSchema>;
export type ClientEnv = z.infer<typeof clientSchema>;

export function serverEnv(): ServerEnv {
  const parsed = serverSchema.safeParse(process.env);
  if (!parsed.success) {
    const missing = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
    throw new Error(`Invalid server environment. Issues with: ${missing}`);
  }
  return parsed.data;
}

export function clientEnv(): ClientEnv {
  const parsed = clientSchema.safeParse({
    NEXT_PUBLIC_SUPABASE_URL: process.env['NEXT_PUBLIC_SUPABASE_URL'],
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY'],
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env['NEXT_PUBLIC_TURNSTILE_SITE_KEY'],
  });
  if (!parsed.success) {
    const missing = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
    throw new Error(`Invalid client environment. Issues with: ${missing}`);
  }
  return parsed.data;
}
